import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetStyleProps } from '@finxone-platform/shared/sys-config-types';

// Inspired by: https://www.lucaspaganini.com/academy/angular-control-value-accessor-custom-form-components#control-value-accessor
@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'workflow-finx-input',
  templateUrl: './workflow-finx-input.component.html',
  styleUrls: ['./workflow-finx-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkflowFinxInputComponent),
      multi: true,
    },
  ],
})
export class WorkflowFinxInputComponent implements ControlValueAccessor, OnInit {
  el = inject(ElementRef);
  private cd = inject(ChangeDetectorRef);

  isFocused = false;
  @Input() className = '';
  @Input() name = '';
  @Input() label = '';
  @Input() required = false;
  @Input() type = 'text';
  @Input() value: boolean | string | number | undefined | null = '';
  @Input() placeholder = '';
  @Input() maxLength = 1000;
  @Input() visible = true;
  @Input() error = '';
  @Input() readOnly = false;
  @Input() checked = false;
  @Input() minValue: number;
  @Input() maxValue: number;
  @Input() textColor: WidgetStyleProps;
  @Input() prefixIcon = false;
  @Input() textAlignCenter = false;
  @Input() bgColorLighterblue = false;
  @Input() isTextBold = false;
  @Input() isFocusInput = false;
  @Input() formGroup: FormGroup;
  @Input() formControlName: string;
  @Input() step: number;
  @Input() isDisabled = false;
  @Input() ariaLabel = '';

  @ViewChild('inputField', { static: false })
  inputField: ElementRef<HTMLInputElement>;
  formControlStatus: boolean;
  @Output() valueChange = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onEnterClickvalueChange = new EventEmitter<string>();
  @Output() errorChange = new EventEmitter<string>();
  @Output() escapeClicked = new EventEmitter();
  @Output() focusEvent = new EventEmitter<boolean>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() checkboxValueChanges = new EventEmitter<any>();
  public readonly inputStringControl = new FormControl();
  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelTouched: Function = () => {};
  ngOnInit(): void {
    this.inputStringControl.valueChanges.subscribe((inputString) => {
      // For checkbox, ensure boolean value is passed
      if (this.type === 'checkbox') {
        this.onModelChange(!!inputString);
      } else {
        this.onModelChange(inputString);
      }
      this.onModelTouched();
      this.cd.detectChanges();
    });
    if (this.formGroup && this.formControlName) {
      const formControl = this.formGroup.get(this.formControlName);
      if (formControl) {
        this.inputStringControl.setValue(formControl.value);
        this.formControlStatusValue();
      }
    }
  }

  // This method updates the formControlStatus based on the validity of the associated form control.
  formControlStatusValue() {
    if (this.formGroup && this.formControlName) {
      const formControl = this.formGroup.get(this.formControlName);
      this.formControlStatus = formControl ? formControl.invalid : false;
    }
  }
  writeValue(value: any): void {
    value = value ?? (this.type === 'checkbox' ? false : '');
    this.value = value;
    // For checkbox, ensure boolean value
    if (this.type === 'checkbox') {
      this.inputStringControl.setValue(!!value);
    } else {
      this.inputStringControl.setValue(value);
    }
    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputStringControl.disable();
    } else {
      this.inputStringControl.enable();
    }
    this.cd.markForCheck();
  }

  onKeypress = () => {
    this.error = '';
  };

  onFocus = () => {
    this.isFocused = true;
    this.onModelTouched(this.value);
  };

  onBlur = () => {
    this.isFocused = false;
    this.focusEvent.emit(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange = (event: any) => {
    if (this.type === 'checkbox') {
      this.value = event.target.checked;
      this.valueChange.emit(this.value);
      this.checkboxValueChanges.emit({
        checked: event.target.checked,
        value: this.value,
      });
      this.onModelChange(this.value);
    } else {
      this.value = this.compareValueWithMinValueIfPresent(event.target.value);
      this.valueChange.emit(this.value?.toString() ?? '');
      this.checkboxValueChanges.emit({
        checked: event.target.checked,
        value: this.value ?? '',
      });
      this.onModelChange(this.value);
    }
    this.formControlStatusValue();
    this.error = '';
    this.errorChange.emit(event);
  };

  public onKeyup = (event: KeyboardEvent) => {
    if (event.key == 'Enter') {
      this.onEnterClick(event);
    } else if (event.key == 'Escape') {
      this.escapeClicked.emit();
    } else {
      this.onModelChange(this.value);
      this.error = '';
      this.errorChange.emit('');
    }
  };

  private onEnterClick = (event: KeyboardEvent) => {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onEnterClickvalueChange.emit(this.value?.toString() ?? '');

    this.onModelChange(this.value);
    this.error = '';
    this.errorChange.emit('');
  };

  onNgModelChange = (event: boolean | string | number | undefined | null) => {
    if (this.type === 'checkbox') {
      this.value = !!event;
      this.valueChange.emit(this.value);
      this.onModelChange(this.value);
    } else {
      this.value = event;
      this.valueChange.emit(this.value?.toString() ?? '');
      this.onModelChange(this.value);
    }
    this.formControlStatusValue();
    this.error = '';
    this.errorChange.emit('');
  };

  focusInputField() {
    if (this.inputField) {
      this.inputField.nativeElement.focus();
    }
  }

  private compareValueWithMinValueIfPresent(value: string): string {
    if (this.type !== 'number') return value;
    if (this.minValue !== undefined && Number(value) < this.minValue) {
      return this.minValue.toString();
    }
    return value;
  }
}
